import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { TextField } from "@mui/material";
import clsx from "clsx";
import Text from "../../styleguide/Text";

interface DatePicker {
  label?: string;
  placeholder?: string;
  handleChangeDate?: (newValue: Date | null) => void;
  value?: Date | string | null;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  withVerticalSpacer?: boolean;
  isRequired?: boolean;
  touched?: boolean;
  error?: string;
  textInputClassName?: string;
  dateFormat?: string;
  fullWith?: boolean;
  gutterBottom?: boolean;
  maxDate?: Date;
  minDate?: Date;
  withClearData?: boolean;
  size?: "small" | "medium";
  views?: ("year" | "month" | "day")[];
  onOpen?: () => void;
  onClose?: () => void;
}

const DatePicker = ({
  label,
  placeholder,
  handleChangeDate,
  value,
  disabled,
  className,
  style,
  withVerticalSpacer,
  isRequired,
  textInputClassName,
  dateFormat = "D/MM/yyyy",
  fullWith = true,
  gutterBottom = true,
  maxDate,
  minDate,
  withClearData,
  touched,
  error,
  size = "medium",
  views,
  onOpen,
  onClose,
}: DatePicker) => {
  const handleChange = (newValue: Date | null) => {
    if (withClearData) {
      handleChangeDate && handleChangeDate(newValue);
      return;
    }
    if (newValue) {
      handleChangeDate && handleChangeDate(newValue);
    }
  };
  return (
    <div
      className={`${fullWith ? "w-full" : ""} ${
        withVerticalSpacer ? "px-1" : ""
      } ${className}`}
      style={style}>
      <MuiDatePicker
        componentsProps={{
          actionBar: {
            actions: withClearData ? ["clear"] : [],
          },
        }}
        value={value || null}
        inputFormat={dateFormat}
        views={views}
        onOpen={onOpen}
        onClose={onClose}
        disableMaskedInput
        onChange={handleChange}
        disabled={disabled}
        maxDate={maxDate}
        minDate={minDate}
        className={clsx(
          "mb-3 w-full rounded-control bg-surface-subtle [&_.MuiOutlinedInput-root]:h-[48px] [&_.MuiOutlinedInput-root]:rounded-control [&_.MuiOutlinedInput-root]:bg-surface-subtle [&_.MuiOutlinedInput-input]:py-0",
          textInputClassName,
        )}
        renderInput={(params) => (
          <div className={`flex flex-col ${gutterBottom ? "mb-7" : ""}`}>
            {label && (
              <label className="mb-2 flex items-center text-slate-900">
                <Text variant="body1" isBold>
                  {label}
                </Text>
                {isRequired && <span className="ml-1 text-danger">*</span>}
              </label>
            )}
            <TextField
              sx={{
                "& fieldset": { border: "none" },
              }}
              size={size}
              variant="outlined"
              placeholder={placeholder}
              {...params}
              inputProps={{
                ...params.inputProps,
                readOnly: true,
                ...(placeholder ? { placeholder } : {}),
                ...(label ? { "aria-label": label } : {}),
              }}
              error={Boolean(error && touched)}
            />
          </div>
        )}
      />
      {touched && error && (
        <div className="pt-3">
          <Text variant="body1" role="alert" className="text-danger">
            {error}
          </Text>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
